import React, { useEffect, useRef, useState } from "react";
//import LegalNotice from "./LegalNotice";

import { Instagram, Youtube } from "lucide-react"; //Facebook
//import { FaXTwitter } from "react-icons/fa6";

import InstallAppButton from "./InstallAppButton";
// ─── Utility: intersection-observer reveal hook ───────────────────────────
function useReveal(threshold = 0.15) {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([e]) => {
        if (e.isIntersecting) {
          setVisible(true);
          obs.disconnect();
        }
      },
      { threshold },
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [threshold]);
  return [ref, visible];
}

// ─── Stat Card ────────────────────────────────────────────────────────────
const StatCard = ({ number, label, delay }) => {
  const [ref, visible] = useReveal();
  return (
    <div
      ref={ref}
      style={{
        padding: "2.5rem 1.5rem",
        textAlign: "center",
        borderRight: "1px solid #ede9fe",
        transform: visible ? "translateY(0)" : "translateY(24px)",
        opacity: visible ? 1 : 0,
        transition: `transform 0.7s cubic-bezier(.22,1,.36,1) ${delay}ms, opacity 0.6s ease ${delay}ms`,
      }}
    >
      <div
        style={{
          fontSize: "clamp(2rem, 4vw, 2.8rem)",
          fontFamily: "'Playfair Display', serif",
          fontWeight: "900",
          background: "linear-gradient(135deg, #667eea, #764ba2)",
          WebkitBackgroundClip: "text",
          WebkitTextFillColor: "transparent",
          letterSpacing: "-0.03em",
          lineHeight: 1,
          marginBottom: "0.5rem",
        }}
      >
        {number}
      </div>
      <div
        style={{
          color: "#9ca3af",
          fontSize: "0.78rem",
          letterSpacing: "0.14em",
          textTransform: "uppercase",
          fontWeight: 600,
        }}
      >
        {label}
      </div>
    </div>
  );
};

// ─── Feature Card ─────────────────────────────────────────────────────────
const FeatureCard = ({ icon, title, desc, delay }) => {
  const [ref, visible] = useReveal();
  const [hov, setHov] = useState(false);
  return (
    <div
      ref={ref}
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      style={{
        background: hov
          ? "linear-gradient(135deg,rgba(102,126,234,0.05),rgba(118,75,162,0.08))"
          : "#fff",
        border: hov ? "1.5px solid #c4b5fd" : "1.5px solid #ede9fe",
        borderRadius: "16px",
        padding: "2.25rem 2rem",
        transform: visible
          ? hov
            ? "translateY(-5px)"
            : "translateY(0)"
          : "translateY(28px)",
        opacity: visible ? 1 : 0,
        transition: `transform 0.6s cubic-bezier(.22,1,.36,1) ${delay}ms, opacity 0.6s ease ${delay}ms, background 0.3s, border 0.3s`,
        boxShadow: hov
          ? "0 12px 40px rgba(118,75,162,0.1)"
          : "0 2px 12px rgba(0,0,0,0.04)",
        cursor: "default",
      }}
    >
      <div
        style={{
          width: 52,
          height: 52,
          borderRadius: "14px",
          background:
            "linear-gradient(135deg,rgba(102,126,234,0.1),rgba(118,75,162,0.16))",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontSize: "1.5rem",
          marginBottom: "1.25rem",
        }}
      >
        {icon}
      </div>
      <h3
        style={{
          fontFamily: "'Playfair Display', serif",
          fontSize: "1.2rem",
          fontWeight: "700",
          color: "#1e1b4b",
          marginBottom: "0.6rem",
        }}
      >
        {title}
      </h3>
      <p
        style={{
          color: "#6b7280",
          lineHeight: 1.75,
          fontSize: "0.92rem",
          margin: 0,
        }}
      >
        {desc}
      </p>
    </div>
  );
};

// ─── Value Row ────────────────────────────────────────────────────────────
const ValueRow = ({ idx, title, desc }) => {
  const [ref, visible] = useReveal();
  return (
    <div
      ref={ref}
      style={{
        display: "grid",
        gridTemplateColumns: "2.5rem 1fr",
        gap: "1.25rem",
        alignItems: "start",
        padding: "1.75rem 0",
        borderBottom: "1px solid #f3f0ff",
        transform: visible ? "translateX(0)" : "translateX(-24px)",
        opacity: visible ? 1 : 0,
        transition: `transform 0.65s cubic-bezier(.22,1,.36,1) ${idx * 90}ms, opacity 0.65s ease ${idx * 90}ms`,
      }}
    >
      <span
        style={{
          fontFamily: "'Playfair Display', serif",
          fontSize: "1rem",
          fontWeight: "700",
          background: "linear-gradient(135deg,#667eea,#764ba2)",
          WebkitBackgroundClip: "text",
          WebkitTextFillColor: "transparent",
          paddingTop: "0.1rem",
        }}
      >
        0{idx + 1}
      </span>
      <div>
        <div
          style={{
            fontWeight: "700",
            fontSize: "1.05rem",
            color: "#1e1b4b",
            marginBottom: "0.35rem",
          }}
        >
          {title}
        </div>
        <div
          style={{ color: "#6b7280", lineHeight: 1.75, fontSize: "0.92rem" }}
        >
          {desc}
        </div>
      </div>
    </div>
  );
};
// ─── Footer Link Item (Hook Safe) ───────────────────────────────────────
const FooterLink = ({ children, onClick, href }) => {
  const [hover, setHover] = useState(false);

  return (
    <a
      href={href}
      onClick={onClick}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        color: hover ? "#7c3aed" : "#4b5563",
        textDecoration: "none",
        fontSize: "0.875rem",
        fontWeight: hover ? "600" : "400",
        cursor: "pointer",
        transition: "color 0.2s",
        display: "flex",
        alignItems: "center",
        gap: "0.4rem",
      }}
    >
      {hover && <span style={{ fontSize: "0.6rem", color: "#a78bfa" }}>▶</span>}
      {children}
    </a>
  );
};

// ─── Vybex Logo ──────────────────────────────────────────────────────────────
const VybexLogo = () => {
  const ref = useRef(null);
  useEffect(() => {
    const link = document.createElement("link");
    link.href =
      "https://fonts.googleapis.com/css2?family=Michroma&display=swap";
    link.rel = "stylesheet";
    document.head.appendChild(link);
    const container = ref.current;
    if (!container) return;
    container.innerHTML = ""; // prevent double render in StrictMode
    "VYBEX".split("").forEach((ch, i) => {
      const s = document.createElement("span");
      Object.assign(s.style, {
        fontFamily: "'Michroma', sans-serif",
        background: "linear-gradient(135deg, #00d4ff, #7c3aed, #ec4899)",
        WebkitBackgroundClip: "text",
        WebkitTextFillColor: "transparent",
        backgroundClip: "text",
        opacity: "0",
        transform: "translateY(6px) scale(0.85)",
        display: "inline-block",
        transition: `opacity 0.5s cubic-bezier(0.16,1,0.3,1) ${i * 80}ms, transform 0.5s cubic-bezier(0.16,1,0.3,1) ${i * 80}ms`,
      });
      s.textContent = ch;
      container.appendChild(s);
      setTimeout(
        () => {
          s.style.opacity = "1";
          s.style.transform = "translateY(0) scale(1)";
        },
        100 + i * 80,
      );
    });
  }, []);
  return (
    <span
      ref={ref}
      style={{
        display: "inline-flex",
        letterSpacing: "0.18em",
        fontSize: "0.9rem",
        background: "#0a0a18",
        padding: "4px 12px",
        borderRadius: "999px",
        border: "1px solid rgba(124,58,237,0.35)",
      }}
    />
  );
};

// ─── Social Icon (Hook Safe) ─────────────────────────────────────────────
const SocialIcon = ({ s }) => {
  const [hover, setHover] = useState(false);

  return (
    <a
      href={s.href}
      target="_blank"
      rel="noopener noreferrer"
      title={s.name}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        width: 36,
        height: 36,
        borderRadius: "10px",
        border: hover ? `1.5px solid ${s.hoverBg}` : "1.5px solid #ede9fe",
        background: hover ? `${s.hoverBg}14` : "#fafafa",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontSize: "1rem",
        textDecoration: "none",
        transition: "all 0.2s",
        transform: hover ? "translateY(-2px)" : "none",
      }}
    >
      {s.icon}
    </a>
  );
};
// ─── Footer (inline, light theme) ────────────────────────────────────────
const Footer = ({ onNavigate }) => {
  const currentYear = new Date().getFullYear();

  const handleNavigation = (view) => {
    if (onNavigate) {
      onNavigate(view);
    } else {
      window.location.href = view.startsWith("/") ? view : `/${view}`;
    }
  };

  const socials = [
    {
      name: "Instagram",
      icon: <Instagram size={18} color="#E1306C" />,
      href: "https://www.instagram.com/noryq.ai",
      hoverBg: "#E1306C",
    },

    {
      name: "YouTube",
      icon: <Youtube size={18} color="#aa023aff" />,
      href: "https://youtube.com/@noryq-ai",
      hoverBg: "#FF0000",
    },
  ];

  const quickLinks = [
    { name: "Home", view: "home" },
    { name: "Trending", view: "trending" },
    { name: "Premium", view: "premium" },
    { name: "About Us", href: "/about" },
    { name: "Why Us", href: "/whyus" },
  ];

  const legalLinks = [
    { name: "Privacy Policy", href: "/privacy" },
    { name: "Terms of Service", href: "/terms" },
    { name: "Cookie Policy", href: "/cookies" },
    { name: "Refund Policy", href: "/refund" },
    { name: "Grievance Officer", href: "/privacy#grievance" },
    { name: "Contact Us", href: "/contact" },
  ];

  return (
    <footer
      className="au-footer"
      style={{
        background: "#fff",
        borderTop: "1px solid #ede9fe",
        padding: "4rem 2rem 2rem",
        marginTop: "0",
      }}
    >
      <div style={{ maxWidth: "1100px", margin: "0 auto" }}>
        {/* Top grid */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
            gap: "3rem",
            marginBottom: "3rem",
          }}
        >
          {/* Brand */}
          <div style={{ gridColumn: "span 1" }}>
            <a
              href="/"
              style={{
                textDecoration: "none",
                display: "inline-block",
                marginBottom: "1rem",
              }}
            >
              <span
                style={{
                  fontFamily: "'Playfair Display', serif",
                  fontWeight: "900",
                  fontSize: "1.5rem",
                  letterSpacing: "0.2em",
                  background:
                    "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                }}
              >
                {/* Logo Section - Fixed Size */}
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "0.75rem",
                    cursor: "pointer",
                    flexShrink: 0, // Prevents logo from shrinking
                  }}
                >
                  <img
                    src="/logo.png"
                    alt="NORYQ"
                    style={{
                      height: "50px",
                      width: "auto", // Maintains aspect ratio
                      display: "block",
                    }}
                  />
                </div>
              </span>
            </a>
            <p
              style={{
                color: "#6b7280",
                lineHeight: 1.7,
                fontSize: "0.875rem",
                marginBottom: "1.5rem",
              }}
            >
              Your AI-powered movie and TV show recommendation companion. Find
              your perfect watch based on your mood.
            </p>
            {/* Install CTA card */}
            <style>{`
              @media (max-width: 480px) {
                .noryq-install-card { padding: 1rem !important; }
                .noryq-install-card p { font-size: 0.88rem !important; }
                .noryq-install-label { display: none !important; }
              }
            `}</style>
            <div
              className="noryq-install-card"
              style={{
                background:
                  "linear-gradient(135deg, rgba(102,126,234,0.06) 0%, rgba(118,75,162,0.1) 100%)",
                border: "1.5px solid #ede9fe",
                borderRadius: "16px",
                padding: "1.1rem 1.25rem",
                display: "flex",
                flexDirection: "column",
                gap: "0.85rem",
                width: "100%",
                boxSizing: "border-box",
              }}
            >
              {/* Label row */}
              <div
                className="noryq-install-label"
                style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}
              ></div>

              {/* Headline */}
              <p
                className="noryq-install-headline"
                style={{
                  fontFamily: "'Playfair Display', serif",
                  fontSize: "0.95rem",
                  fontWeight: "700",
                  fontStyle: "italic",
                  color: "#1e1b4b",
                  lineHeight: 1.35,
                  margin: 0,
                }}
              >
                Take NORYQ everywhere.
                <br />
                <span
                  style={{
                    background: "linear-gradient(135deg,#667eea,#764ba2)",
                    WebkitBackgroundClip: "text",
                    WebkitTextFillColor: "transparent",
                  }}
                >
                  Install it in one tap.
                </span>
              </p>

              {/* Button — full width, never overflows */}
              <div style={{ width: "100%", minWidth: 0 }}>
                <InstallAppButton />
              </div>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "0.6rem",
                marginBottom: "1.25rem",
              }}
            >
              <div
                style={{
                  width: "1.25rem",
                  height: "2px",
                  background: "linear-gradient(90deg,#667eea,#764ba2)",
                  borderRadius: "2px",
                }}
              />
              <h4
                style={{
                  fontSize: "0.75rem",
                  fontWeight: "700",
                  color: "#7c3aed",
                  letterSpacing: "0.14em",
                  textTransform: "uppercase",
                  margin: 0,
                }}
              >
                Quick Links
              </h4>
            </div>
            <ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
              {quickLinks.map((link) => (
                <li key={link.name} style={{ marginBottom: "0.6rem" }}>
                  <FooterLink
                    href={link.href || "#"}
                    onClick={
                      link.view
                        ? (e) => {
                            e.preventDefault();
                            handleNavigation(link.view);
                          }
                        : undefined
                    }
                  >
                    {link.name}
                  </FooterLink>
                </li>
              ))}
            </ul>
          </div>

          {/* Legal */}
          <div>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "0.6rem",
                marginBottom: "1.25rem",
              }}
            >
              <div
                style={{
                  width: "1.25rem",
                  height: "2px",
                  background: "linear-gradient(90deg,#667eea,#764ba2)",
                  borderRadius: "2px",
                }}
              />
              <h4
                style={{
                  fontSize: "0.75rem",
                  fontWeight: "700",
                  color: "#7c3aed",
                  letterSpacing: "0.14em",
                  textTransform: "uppercase",
                  margin: 0,
                }}
              >
                Legal
              </h4>
            </div>
            <ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
              {legalLinks.map((link) => (
                <li key={link.name} style={{ marginBottom: "0.6rem" }}>
                  <FooterLink href={link.href}>{link.name}</FooterLink>
                </li>
              ))}
            </ul>
          </div>

          {/* Stay updated */}
          <div>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "0.6rem",
                marginBottom: "1.25rem",
              }}
            >
              <div
                style={{
                  width: "1.25rem",
                  height: "2px",
                  background: "linear-gradient(90deg,#667eea,#764ba2)",
                  borderRadius: "2px",
                }}
              />
              <h4
                style={{
                  fontSize: "0.75rem",
                  fontWeight: "700",
                  color: "#7c3aed",
                  letterSpacing: "0.14em",
                  textTransform: "uppercase",
                  margin: 0,
                }}
              >
                Stay Updated
              </h4>
            </div>
            <p
              style={{
                color: "#6b7280",
                fontSize: "0.875rem",
                lineHeight: 1.7,
                marginBottom: "1rem",
              }}
            >
              Follow us for the latest recommendations and feature drops.
            </p>
            {/* Socials */}
            <div
              style={{ display: "flex", gap: "0.6rem", marginBottom: "1rem" }}
            >
              {socials.map((s) => (
                <SocialIcon key={s.name} s={s} />
              ))}
            </div>
            <div
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: "0.5rem",
                background:
                  "linear-gradient(135deg,rgba(102,126,234,0.07),rgba(118,75,162,0.1))",
                border: "1px solid #c4b5fd",
                borderRadius: "999px",
                padding: "0.4rem 1rem",
              }}
            >
              <span
                style={{
                  width: 6,
                  height: 6,
                  borderRadius: "50%",
                  background: "linear-gradient(135deg,#667eea,#764ba2)",
                }}
              />

              <span
                style={{
                  fontSize: "0.75rem",
                  color: "#7c3aed",
                  fontWeight: 600,
                }}
              >
                v1.0.0 — Live
              </span>
            </div>
          </div>
        </div>

        {/* Divider */}
        <div
          style={{
            height: "1px",
            background:
              "linear-gradient(90deg, transparent, #c4b5fd 40%, #a78bfa 60%, transparent)",
            marginBottom: "1.5rem",
          }}
        />

        {/* Bottom bar */}
        <div
          className="au-footer-bottom"
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            flexWrap: "wrap",
            gap: "0.75rem",
          }}
        >
          <p style={{ margin: 0, fontSize: "0.82rem", color: "#9ca3af" }}>
            © {currentYear} NORYQ. All rights reserved. Made with ❤️ in India
          </p>
          <div
            style={{
              display: "flex",
              gap: "1rem",
              fontSize: "0.82rem",
              color: "#9ca3af",
              alignItems: "center",
            }}
          >
            <span>Powered by</span>
            <a
              href="https://vybex1.netlify.app"
              target="_blank"
              rel="noopener noreferrer"
              style={{ textDecoration: "none" }}
            >
              <VybexLogo />
            </a>

            <span style={{ color: "#c4b5fd" }}>•</span>
            <span>v1.0.0</span>
          </div>
        </div>
        {/* <LegalNotice /> */}
      </div>
    </footer>
  );
};

// ─── Main Component ───────────────────────────────────────────────────────
const AboutUs = () => {
  const [heroVisible, setHeroVisible] = useState(false);
  useEffect(() => {
    document.title = "About Us | NORYQ";
  }, []);
  useEffect(() => {
    const t = setTimeout(() => setHeroVisible(true), 80);
    return () => clearTimeout(t);
  }, []);

  const [storyRef, storyVisible] = useReveal();
  const [missionRef, missionVisible] = useReveal();

  const stats = [
    { number: "10K+", label: "Happy Users" },
    { number: "100K+", label: "Recommendations" },
    { number: "98%", label: "Satisfaction" },
    { number: "24/7", label: "Available" },
  ];

  const features = [
    {
      icon: "✦",
      title: "AI-Powered Mood Engine",
      desc: "Advanced AI understands your emotional state and current vibe to surface content that actually resonates — not just genre matches.",
    },
    {
      icon: "⚡",
      title: "Instant Results",
      desc: "From zero to perfect recommendation in seconds. No more endless scrolling through thousands of titles.",
    },
    {
      icon: "◎",
      title: "Built by Cinephiles",
      desc: "Crafted by people who've wasted too many Friday nights hunting for something to watch. Every feature is born from real frustration.",
    },
  ];

  const values = [
    {
      title: "User First — Always",
      desc: "Every decision we make starts with one question: Is this genuinely good for our users? Not good for metrics. Good for people.",
    },
    {
      title: "Zero Hidden Agendas",
      desc: "No paid promotions. No sponsored placements. Our recommendations are purely algorithmic, driven entirely by your preferences.",
    },
    {
      title: "Relentless Improvement",
      desc: "We ship updates constantly. Our AI learns from every interaction, getting sharper at understanding the nuance of human mood.",
    },
    {
      title: "Privacy by Design",
      desc: "Your data is yours. We operate on the principle that personalization and privacy aren't opposites — they can coexist.",
    },
  ];

  return (
    <div
      style={{
        minHeight: "100vh",
        background: "#fafafa",
        color: "#1e1b4b",
        fontFamily: "'DM Sans', sans-serif",
        overflowX: "hidden",
      }}
    >
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,700;0,900;1,700;1,900&family=DM+Sans:wght@300;400;500;600;700&display=swap');
        * { box-sizing: border-box; margin: 0; padding: 0; }
        ::-webkit-scrollbar { width: 4px; }
        ::-webkit-scrollbar-track { background: #f5f3ff; }
        ::-webkit-scrollbar-thumb { background: #a78bfa; border-radius: 2px; }
        @keyframes pulse-dot { 0%,100%{opacity:1} 50%{opacity:0.3} }
        @keyframes float-blob { 0%,100%{transform:translateY(0) scale(1)} 50%{transform:translateY(-18px) scale(1.04)} }

        /* ── Responsive ────────────────────────────────────────── */
        @media (max-width: 480px) { .au-nav { padding: 0 1.25rem !important; } }
        @media (max-width: 640px) { .au-hero-inner { padding: 4.5rem 1.25rem 3.5rem !important; } }

        .au-stats-grid { display: grid; grid-template-columns: repeat(4, 1fr); }
        @media (max-width: 760px) {
          .au-stats-grid { grid-template-columns: repeat(2, 1fr) !important; }
          .au-stats-grid > div { border-right: none !important; border-bottom: 1px solid #ede9fe; }
          .au-stats-grid > div:nth-child(odd) { border-right: 1px solid #ede9fe !important; }
          .au-stats-grid > div:last-child { border-bottom: none; }
        }
        @media (max-width: 400px) {
          .au-stats-grid { grid-template-columns: 1fr !important; }
          .au-stats-grid > div { border-right: none !important; }
        }

        .au-story-grid { display: grid; grid-template-columns: 1fr 1.4fr; gap: 5rem; align-items: start; }
        @media (max-width: 860px) { .au-story-grid { grid-template-columns: 1fr !important; gap: 2.5rem !important; } }

        @media (max-width: 640px) { .au-section { padding: 3.5rem 1.25rem !important; } }
        @media (max-width: 480px) { .au-feature-card { padding: 1.5rem !important; } }

        @media (max-width: 640px) {
          .au-mission { padding: 4rem 1.25rem !important; }
          .au-mission-quote-mark { display: none !important; }
        }

        .au-values-grid { display: grid; grid-template-columns: 1fr 2fr; gap: 5rem; align-items: start; }
        @media (max-width: 860px) {
          .au-values-grid { grid-template-columns: 1fr !important; gap: 2rem !important; }
          .au-values-heading { position: static !important; }
        }
        @media (max-width: 480px) {
          .au-value-row { padding: 1.25rem 0 !important; }
          .au-value-row-inner { gap: 0.75rem !important; }
        }

        @media (max-width: 640px) {
          .au-cta-card { padding: 3rem 1.5rem !important; }
          .au-cta-btn { padding: 1rem 1.75rem !important; width: 100%; }
          .au-footer { padding: 3rem 1.25rem 1.5rem !important; }
          .au-footer-bottom { flex-direction: column !important; align-items: flex-start !important; gap: 0.5rem !important; }
        }
        @media (max-width: 400px) {
          .noryq-install-label { display: none !important; }
          .noryq-install-card { padding: 1rem !important; }
        }
      `}</style>

      {/* ── NAV ──────────────────────────────────────────────────────── */}
      <nav
        className="au-nav"
        style={{
          position: "sticky",
          top: 0,
          zIndex: 50,
          background: "rgba(250,250,250,0.88)",
          backdropFilter: "blur(18px)",
          borderBottom: "1px solid #ede9fe",
          padding: "0 2.5rem",
          height: "64px",
          display: "flex",
          alignItems: "center",
        }}
      >
        <a href="/" style={{ textDecoration: "none" }}>
          {/* Logo Section - Fixed Size */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "0.75rem",
              cursor: "pointer",
              flexShrink: 0, // Prevents logo from shrinking
            }}
          >
            <img
              src="/logo.png"
              alt="NORYQ"
              style={{
                height: "50px",
                width: "auto", // Maintains aspect ratio
                display: "block",
              }}
            />
          </div>
        </a>
      </nav>

      {/* ── HERO ─────────────────────────────────────────────────────── */}
      <div
        style={{
          position: "relative",
          overflow: "hidden",
          background: "#fff",
          borderBottom: "1px solid #ede9fe",
        }}
      >
        <div
          style={{
            position: "absolute",
            top: "-15%",
            right: "-8%",
            width: "52vw",
            height: "52vw",
            maxWidth: "680px",
            maxHeight: "680px",
            borderRadius: "50%",
            background:
              "radial-gradient(circle, rgba(102,126,234,0.1) 0%, rgba(118,75,162,0.06) 50%, transparent 70%)",
            animation: "float-blob 8s ease-in-out infinite",
            pointerEvents: "none",
          }}
        />
        <div
          style={{
            position: "absolute",
            bottom: "-10%",
            left: "-6%",
            width: "38vw",
            height: "38vw",
            maxWidth: "480px",
            maxHeight: "480px",
            borderRadius: "50%",
            background:
              "radial-gradient(circle, rgba(118,75,162,0.07) 0%, transparent 70%)",
            animation: "float-blob 11s ease-in-out infinite reverse",
            pointerEvents: "none",
          }}
        />
        <div
          style={{
            position: "absolute",
            inset: 0,
            pointerEvents: "none",
            backgroundImage:
              "radial-gradient(circle, #d8d0f8 1px, transparent 1px)",
            backgroundSize: "28px 28px",
            opacity: 0.35,
          }}
        />

        <div
          className="au-hero-inner"
          style={{
            maxWidth: "1100px",
            margin: "0 auto",
            padding: "8rem 2rem 7rem",
            position: "relative",
            zIndex: 1,
            textAlign: "center",
          }}
        >
          {/* Badge */}
          <div
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: "0.5rem",
              background:
                "linear-gradient(135deg,rgba(102,126,234,0.07),rgba(118,75,162,0.12))",
              border: "1px solid #c4b5fd",
              borderRadius: "999px",
              padding: "0.4rem 1.1rem",
              marginBottom: "2.5rem",
              transform: heroVisible ? "translateY(0)" : "translateY(14px)",
              opacity: heroVisible ? 1 : 0,
              transition: "transform 0.6s ease 0.1s, opacity 0.6s ease 0.1s",
            }}
          >
            <span
              style={{
                width: 7,
                height: 7,
                borderRadius: "50%",
                background: "linear-gradient(135deg,#667eea,#764ba2)",
                animation: "pulse-dot 1.8s infinite",
              }}
            />
            <span
              style={{
                fontSize: "0.74rem",
                letterSpacing: "0.12em",
                textTransform: "uppercase",
                color: "#7c3aed",
                fontWeight: 700,
              }}
            >
              The Story Behind NORYQ
            </span>
          </div>

          <h1
            style={{
              fontFamily: "'Playfair Display', serif",
              fontSize: "clamp(2.8rem, 7vw, 6rem)",
              fontWeight: "900",
              lineHeight: 1.05,
              letterSpacing: "-0.03em",
              color: "#1e1b4b",
              marginBottom: "1.75rem",
              transform: heroVisible ? "translateY(0)" : "translateY(22px)",
              opacity: heroVisible ? 1 : 0,
              transition:
                "transform 0.75s cubic-bezier(.22,1,.36,1) 0.2s, opacity 0.75s ease 0.2s",
            }}
          >
            We Killed
            <br />
            <span
              style={{
                fontStyle: "italic",
                background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
              }}
            >
              Decision
            </span>
            <br />
            Fatigue.
          </h1>

          <p
            style={{
              fontSize: "clamp(1rem, 1.8vw, 1.15rem)",
              lineHeight: 1.8,
              color: "#6b7280",
              maxWidth: "540px",
              margin: "0 auto 3rem",
              transform: heroVisible ? "translateY(0)" : "translateY(18px)",
              opacity: heroVisible ? 1 : 0,
              transition: "transform 0.7s ease 0.35s, opacity 0.7s ease 0.35s",
            }}
          >
            Your AI-powered companion for discovering exactly the right movie or
            TV show — matched to your mood, not just your history.
          </p>

          <button
            onClick={() => (window.location.href = "/")}
            style={{
              background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
              color: "#fff",
              border: "none",
              padding: "1rem 2.5rem",
              borderRadius: "12px",
              fontSize: "0.9rem",
              fontWeight: "700",
              letterSpacing: "0.06em",
              cursor: "pointer",
              fontFamily: "'DM Sans', sans-serif",
              boxShadow: "0 8px 28px rgba(102,126,234,0.3)",
              transform: heroVisible ? "translateY(0)" : "translateY(18px)",
              opacity: heroVisible ? 1 : 0,
              transition:
                "transform 0.2s, box-shadow 0.2s, opacity 0.7s ease 0.5s",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = "translateY(-2px)";
              e.currentTarget.style.boxShadow =
                "0 16px 44px rgba(118,75,162,0.38)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = "translateY(0)";
              e.currentTarget.style.boxShadow =
                "0 8px 28px rgba(102,126,234,0.3)";
            }}
          >
            Start Discovering →
          </button>
        </div>
        <div
          style={{
            height: "2px",
            background:
              "linear-gradient(90deg, transparent, #c4b5fd 40%, #a78bfa 60%, transparent)",
          }}
        />
      </div>

      {/* ── STATS ────────────────────────────────────────────────────── */}
      <div style={{ background: "#fff", borderBottom: "1px solid #ede9fe" }}>
        <div
          className="au-stats-grid"
          style={{
            maxWidth: "1100px",
            margin: "0 auto",
          }}
        >
          {stats.map((s, i) => (
            <StatCard key={i} {...s} delay={i * 80} />
          ))}
        </div>
      </div>

      {/* ── STORY ────────────────────────────────────────────────────── */}
      <div
        className="au-section"
        style={{ maxWidth: "1100px", margin: "0 auto", padding: "6rem 2rem" }}
      >
        <div
          ref={storyRef}
          className="au-story-grid"
          style={{
            alignItems: "start",
          }}
        >
          <div
            style={{
              transform: storyVisible ? "translateX(0)" : "translateX(-32px)",
              opacity: storyVisible ? 1 : 0,
              transition:
                "transform 0.8s cubic-bezier(.22,1,.36,1), opacity 0.8s ease",
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "0.75rem",
                marginBottom: "1.5rem",
              }}
            >
              <div
                style={{
                  width: "2rem",
                  height: "2px",
                  background: "linear-gradient(90deg,#667eea,#764ba2)",
                  borderRadius: "2px",
                }}
              />
              <span
                style={{
                  fontSize: "0.73rem",
                  letterSpacing: "0.18em",
                  textTransform: "uppercase",
                  color: "#7c3aed",
                  fontWeight: 700,
                }}
              >
                Origin
              </span>
            </div>
            <h2
              style={{
                fontFamily: "'Playfair Display', serif",
                fontSize: "clamp(2.2rem, 4vw, 3.5rem)",
                fontWeight: "900",
                lineHeight: 1.1,
                letterSpacing: "-0.03em",
                color: "#1e1b4b",
                marginBottom: "1.5rem",
              }}
            >
              How NORYQ
              <br />
              came to be.
            </h2>
            <div
              style={{
                width: "3rem",
                height: "3px",
                background: "linear-gradient(90deg,#667eea,#764ba2)",
                borderRadius: "2px",
              }}
            />
          </div>

          <div
            style={{
              transform: storyVisible ? "translateX(0)" : "translateX(32px)",
              opacity: storyVisible ? 1 : 0,
              transition:
                "transform 0.8s cubic-bezier(.22,1,.36,1) 0.15s, opacity 0.8s ease 0.15s",
            }}
          >
            {[
              "NORYQ was born from a simple frustration that we've all experienced: spending more time searching for something to watch than actually watching it. Endless scrolling, countless reviews, asking friends — only to rewatch the same show for the third time.",
              "The problem wasn't a lack of content. It was decision fatigue. With thousands of options, choosing becomes overwhelming. That's when we asked: what if AI could understand not just what you like, but how you feel right now?",
              "Using cutting-edge AI, we built a platform that understands your mood, your vibe, and what you actually need in that moment. Whether you need something to lift your spirits or match your contemplative Sunday afternoon — NORYQ gets it.",
            ].map((p, i) => (
              <p
                key={i}
                style={{
                  color: "#4b5563",
                  lineHeight: 1.85,
                  marginBottom: i < 2 ? "1.25rem" : 0,
                  fontSize: "0.97rem",
                }}
              >
                {p}
              </p>
            ))}
          </div>
        </div>
      </div>

      {/* ── FEATURES ─────────────────────────────────────────────────── */}
      <div
        style={{
          background: "#f5f3ff",
          borderTop: "1px solid #ede9fe",
          borderBottom: "1px solid #ede9fe",
        }}
      >
        <div
          style={{ maxWidth: "1100px", margin: "0 auto", padding: "6rem 2rem" }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "0.75rem",
              marginBottom: "1rem",
            }}
          >
            <div
              style={{
                width: "2rem",
                height: "2px",
                background: "linear-gradient(90deg,#667eea,#764ba2)",
                borderRadius: "2px",
              }}
            />
            <span
              style={{
                fontSize: "0.73rem",
                letterSpacing: "0.18em",
                textTransform: "uppercase",
                color: "#7c3aed",
                fontWeight: 700,
              }}
            >
              What sets us apart
            </span>
          </div>
          <h2
            style={{
              fontFamily: "'Playfair Display', serif",
              fontSize: "clamp(1.8rem, 3.5vw, 2.8rem)",
              fontWeight: "900",
              letterSpacing: "-0.03em",
              color: "#1e1b4b",
              marginBottom: "3rem",
            }}
          >
            Built differently, on purpose.
          </h2>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
              gap: "1.25rem",
            }}
          >
            {features.map((f, i) => (
              <FeatureCard key={i} {...f} delay={i * 90} />
            ))}
          </div>
        </div>
      </div>

      {/* ── MISSION ──────────────────────────────────────────────────── */}
      <div
        ref={missionRef}
        className="au-mission"
        style={{
          background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
          position: "relative",
          overflow: "hidden",
          padding: "7rem 2rem",
          transform: missionVisible ? "translateY(0)" : "translateY(28px)",
          opacity: missionVisible ? 1 : 0,
          transition:
            "transform 0.9s cubic-bezier(.22,1,.36,1), opacity 0.9s ease",
        }}
      >
        <div
          className="au-mission-quote-mark"
          style={{
            position: "absolute",
            top: "-2rem",
            left: "2%",
            fontFamily: "'Playfair Display', serif",
            fontSize: "18rem",
            lineHeight: 1,
            color: "rgba(255,255,255,0.06)",
            fontWeight: "900",
            userSelect: "none",
            pointerEvents: "none",
          }}
        >
          "
        </div>
        <div
          style={{
            maxWidth: "780px",
            margin: "0 auto",
            textAlign: "center",
            position: "relative",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: "1rem",
              marginBottom: "2rem",
            }}
          >
            <div
              style={{
                width: "2rem",
                height: "1px",
                background: "rgba(255,255,255,0.4)",
              }}
            />
            <span
              style={{
                fontSize: "0.73rem",
                letterSpacing: "0.18em",
                textTransform: "uppercase",
                color: "rgba(255,255,255,0.75)",
                fontWeight: 700,
              }}
            >
              Our Mission
            </span>
            <div
              style={{
                width: "2rem",
                height: "1px",
                background: "rgba(255,255,255,0.4)",
              }}
            />
          </div>
          <p
            style={{
              fontFamily: "'Playfair Display', serif",
              fontSize: "clamp(1.5rem, 3vw, 2.4rem)",
              fontWeight: "700",
              lineHeight: 1.45,
              color: "#fff",
              letterSpacing: "-0.02em",
            }}
          >
            Finding something great to watch should be as{" "}
            <span
              style={{ fontStyle: "italic", color: "rgba(255,255,255,0.75)" }}
            >
              entertaining
            </span>{" "}
            as the content itself.
          </p>
        </div>
      </div>

      {/* ── VALUES ───────────────────────────────────────────────────── */}
      <div
        className="au-section"
        style={{ maxWidth: "1100px", margin: "0 auto", padding: "6rem 2rem" }}
      >
        <div
          className="au-values-grid"
          style={{
            alignItems: "start",
          }}
        >
          <div>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "0.75rem",
                marginBottom: "1.5rem",
              }}
            >
              <div
                style={{
                  width: "2rem",
                  height: "2px",
                  background: "linear-gradient(90deg,#667eea,#764ba2)",
                  borderRadius: "2px",
                }}
              />
              <span
                style={{
                  fontSize: "0.73rem",
                  letterSpacing: "0.18em",
                  textTransform: "uppercase",
                  color: "#7c3aed",
                  fontWeight: 700,
                }}
              >
                Values
              </span>
            </div>
            <h2
              className="au-values-heading"
              style={{
                fontFamily: "'Playfair Display', serif",
                fontSize: "clamp(2rem, 3.5vw, 3rem)",
                fontWeight: "900",
                lineHeight: 1.15,
                letterSpacing: "-0.03em",
                color: "#1e1b4b",
                position: "sticky",
                top: "5rem",
              }}
            >
              What we
              <br />
              stand for.
            </h2>
          </div>
          <div style={{ borderTop: "1px solid #f3f0ff" }}>
            {values.map((v, i) => (
              <ValueRow key={i} idx={i} title={v.title} desc={v.desc} />
            ))}
          </div>
        </div>
      </div>

      {/* ── CTA ──────────────────────────────────────────────────────── */}
      <div style={{ background: "#f5f3ff", borderTop: "1px solid #ede9fe" }}>
        <div
          className="au-section"
          style={{ maxWidth: "1100px", margin: "0 auto", padding: "6rem 2rem" }}
        >
          <div
            className="au-cta-card"
            style={{
              background: "#fff",
              border: "1.5px solid #ede9fe",
              borderRadius: "24px",
              padding: "5rem 3rem",
              textAlign: "center",
              position: "relative",
              overflow: "hidden",
              boxShadow: "0 4px 40px rgba(102,126,234,0.08)",
            }}
          >
            <div
              style={{
                position: "absolute",
                bottom: 0,
                left: "50%",
                transform: "translateX(-50%)",
                width: "60%",
                height: "120px",
                background:
                  "radial-gradient(ellipse, rgba(118,75,162,0.08) 0%, transparent 70%)",
                pointerEvents: "none",
              }}
            />
            <h2
              style={{
                fontFamily: "'Playfair Display', serif",
                fontSize: "clamp(1.8rem, 4vw, 3rem)",
                fontWeight: "900",
                letterSpacing: "-0.03em",
                color: "#1e1b4b",
                marginBottom: "1rem",
                position: "relative",
              }}
            >
              Ready to find your{" "}
              <span
                style={{
                  fontStyle: "italic",
                  background: "linear-gradient(135deg,#667eea,#764ba2)",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                }}
              >
                perfect watch?
              </span>
            </h2>
            <p
              style={{
                color: "#6b7280",
                fontSize: "1rem",
                lineHeight: 1.75,
                maxWidth: "440px",
                margin: "0 auto 2.5rem",
                position: "relative",
              }}
            >
              Join thousands of users who've already discovered the joy of
              effortless entertainment discovery.
            </p>
            <button
              onClick={() => (window.location.href = "/")}
              className="au-cta-btn"
              style={{
                background: "linear-gradient(135deg,#667eea,#764ba2)",
                color: "#fff",
                border: "none",
                padding: "1.1rem 3rem",
                borderRadius: "12px",
                fontSize: "0.9rem",
                fontWeight: "700",
                letterSpacing: "0.06em",
                cursor: "pointer",
                fontFamily: "'DM Sans', sans-serif",
                boxShadow: "0 8px 28px rgba(102,126,234,0.3)",
                position: "relative",
                transition: "transform 0.2s, box-shadow 0.2s",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = "translateY(-2px)";
                e.currentTarget.style.boxShadow =
                  "0 16px 44px rgba(118,75,162,0.38)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = "translateY(0)";
                e.currentTarget.style.boxShadow =
                  "0 8px 28px rgba(102,126,234,0.3)";
              }}
            >
              Get Started — It's Free
            </button>
          </div>
        </div>
      </div>

      {/* ── FOOTER (inline) ──────────────────────────────────────────── */}
      <Footer />
    </div>
  );
};

export default AboutUs;
